import * as AntdIcon from '@ant-design/icons';
import React from 'react';
import { SYSTEM_MENU_ICONS, SYSTEM_MENU_ROUTES } from '../apis/menu-config';
import type { Menu } from '../types/menu';

export type MenuTreeNode = Menu & { children?: MenuTreeNode[] };
export type ProMenuDataItem = {
  key: string;
  path?: string;
  name?: string;
  icon?: React.ReactNode;
  locale?: false;
  hideInMenu?: boolean;
  target?: string;
  type?: 'group' | 'divider';
  children?: ProMenuDataItem[];
};

const resolveMenuIcon = (
  iconName?: string | null,
): React.ReactNode | undefined => {
  if (!iconName) return undefined;
  const IconComp = AntdIcon[iconName as keyof typeof AntdIcon] as
    | React.ComponentType
    | undefined;
  if (!IconComp) return undefined;
  return React.createElement(IconComp);
};

export const getMenuKey = (menu: Pick<Menu, 'id' | 'route_path'>): string =>
  menu.route_path || `menu-${menu.id}`;

const canonicalSystemRoute = (route?: string | null): string => {
  if (route === '/task/screen/all') return '/task/screen';
  if (route === '/task/task-lists/visible') return '/task/task-lists';
  if (route === '/task/tags/visible') return '/task/tags';
  return route || '';
};

export const buildMenuTree = (list: Menu[]): MenuTreeNode[] => {
  const idMap = new Map<number, MenuTreeNode>();
  const roots: MenuTreeNode[] = [];

  list.forEach((item) => {
    if (!item.id) return;
    idMap.set(item.id, { ...item, children: item.children || [] });
  });

  list.forEach((item) => {
    if (!item.id) return;
    const node = idMap.get(item.id);
    if (!node) return;
    if (item.parent_id && idMap.has(item.parent_id)) {
      idMap.get(item.parent_id)?.children?.push(node);
    } else {
      roots.push(node);
    }
  });

  const order = new Map<string, number>(
    SYSTEM_MENU_ROUTES.map((route, index) => [route, index]),
  );
  const sortItems = (items: MenuTreeNode[]): MenuTreeNode[] =>
    items
      .sort((left, right) => {
        const leftOrder = order.get(canonicalSystemRoute(left.route_path));
        const rightOrder = order.get(canonicalSystemRoute(right.route_path));
        if (leftOrder !== undefined && rightOrder !== undefined) {
          return leftOrder - rightOrder;
        }
        return leftOrder !== undefined ? -1 : rightOrder !== undefined ? 1 : 0;
      })
      .map((item) => ({ ...item, children: sortItems(item.children || []) }));

  return sortItems(roots);
};

export const normalizeMenus = (inputMenus: Menu[]): Menu[] => {
  const managementMenu = inputMenus.find(
    (item) =>
      item.i18n_key === 'menu_management' ||
      item.name === '管理' ||
      item.route_path === 'management' ||
      item.route_path === '/task/management',
  );
  if (!managementMenu?.id) return inputMenus;

  return inputMenus
    .filter((item) => item.id !== managementMenu.id)
    .map((item) =>
      item.parent_id === managementMenu.id
        ? { ...item, parent_id: null }
        : item,
    );
};

export const buildProMenuData = (
  nodes: MenuTreeNode[],
  translate: (key: string) => string,
): ProMenuDataItem[] => {
  return nodes
    .filter((node) => !node.hidden)
    .map((node) => {
      const key = getMenuKey(node);
      const children = node.children
        ? buildProMenuData(node.children, translate)
        : undefined;
      return {
        key,
        path: node.route_path || undefined,
        name: node.i18n_key ? translate(node.i18n_key) : node.name,
        icon: resolveMenuIcon(
          SYSTEM_MENU_ICONS[node.route_path || ''] || node.icon,
        ),
        locale: false,
        hideInMenu: node.hidden,
        target: node.open_target,
        type:
          node.menu_type === 'group' || node.menu_type === 'divider'
            ? node.menu_type
            : undefined,
        children: children?.length ? children : undefined,
      };
    });
};

export const findBestSelectedKey = (
  pathname: string,
  nodes: MenuTreeNode[],
): string => {
  let bestKey = pathname;
  let bestLength = -1;

  const walk = (items: MenuTreeNode[]) => {
    items.forEach((item) => {
      const key = getMenuKey(item);
      if (
        item.route_path &&
        (pathname === item.route_path ||
          pathname.startsWith(`${item.route_path}/`))
      ) {
        const len = item.route_path.length;
        if (len > bestLength) {
          bestLength = len;
          bestKey = key;
        }
      }
      if (item.children?.length) {
        walk(item.children);
      }
    });
  };

  walk(nodes);
  return bestKey;
};

/** Only exact menu destinations determine which ancestors must expand. */
export const findMenuAncestorKeys = (
  selectedKey: string,
  nodes: MenuTreeNode[],
): string[] => {
  for (const node of nodes) {
    if (getMenuKey(node) === selectedKey) return [];
    const descendants = node.children || [];
    if (descendants.some((child) => getMenuKey(child) === selectedKey))
      return [getMenuKey(node)];
    const nested = findMenuAncestorKeys(selectedKey, descendants);
    if (nested.length) return [getMenuKey(node), ...nested];
  }
  return [];
};
